import { parsePhoneNumber } from 'awesome-phonenumber';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  forwardRef,
  input,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { CountryCode, countryCodes, defaultCode } from './country-codes';

@Component({
  selector: 'tf-phone-number',
  imports: [SelectModule, FormsModule, ReactiveFormsModule, InputTextModule],
  templateUrl: './phone-number.component.html',
  styleUrls: ['./phone-number.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneNumberComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PhoneNumberComponent implements ControlValueAccessor {
  readonly label = input<string | undefined>(undefined);
  readonly placeholder = input('');
  readonly invalid = input(false);
  readonly inputId = input<string | undefined>(undefined);
  readonly ariaDescribedBy = input<string | undefined>(undefined);

  readonly form = new FormGroup({
    prefix: new FormControl(defaultCode),
    number: new FormControl(''),
  });

  readonly countryCodes: CountryCode[] = countryCodes;

  // Mirror the prefix control into a signal so `selectedCountry` (and the
  // template that depends on it) stays reactive under OnPush.
  private readonly prefixSignal = toSignal(this.form.controls.prefix.valueChanges, {
    initialValue: this.form.controls.prefix.value,
  });
  readonly selectedCountry = computed<CountryCode | undefined>(() =>
    this.countryCodes.find((c) => c.value === this.prefixSignal()),
  );

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  constructor() {
    this.form.valueChanges.pipe(takeUntilDestroyed()).subscribe((e) => {
      const number = e.prefix && e.number ? e.prefix + e.number : '';
      this.onChange(number);
    });
  }

  writeValue(value: string): void {
    if (value) {
      const parsed = parsePhoneNumber(value);
      // Only split into prefix + local part when we got *both* back. The
      // legacy check on `possibility !== 'unknown'` was too loose: awesome-
      // phonenumber returns `'invalid-country-code'` (not `'unknown'`) for
      // bare strings like "notaphone", so we'd end up setting the prefix to
      // `+undefined`. Verifying both fields keeps unparseable input visible
      // in the local input so the user can correct it.
      const significant = parsed.number?.significant;
      if (parsed.countryCode != null && significant != null) {
        this.form.controls.prefix.setValue(`+${parsed.countryCode}`);
        this.form.controls.number.setValue(significant);
      } else {
        this.form.controls.number.setValue(value);
      }
    } else {
      this.form.controls.number.setValue('');
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  // Intercept paste so a number copied with its country prefix (e.g.
  // "+49 151 234 5678") doesn't leave the dropdown out of sync. We parse the
  // pasted text once, and if it's a recognised international number we
  // promote the prefix into the dropdown and drop it from the input.
  onPaste(event: ClipboardEvent): void {
    const pasted = event.clipboardData?.getData('text') ?? '';
    if (pasted === '') {
      return;
    }
    const trimmed = pasted.trim();
    if (!trimmed.startsWith('+')) {
      return;
    }
    const parsed = parsePhoneNumber(trimmed);
    const detectedPrefix =
      parsed.countryCode != null ? `+${parsed.countryCode}` : null;
    if (
      detectedPrefix == null ||
      !this.countryCodes.some((c) => c.value === detectedPrefix)
    ) {
      return;
    }
    event.preventDefault();
    this.form.controls.prefix.setValue(detectedPrefix);
    this.form.controls.number.setValue(parsed.number?.significant ?? '');
  }

  onBlur(): void {
    this.onTouched();
  }
}
